import { defineStore } from 'pinia'
import CharacterService from '../services/CharacterService'
import { structuredCloneSafe, targetedCloneForMutation } from '@/utils/cloneHelpers'
import { isCacheValid, createCacheInfo } from './cacheUtils'
import { useUserStore } from './userStore'

const applyPatchToObject = (target, patch) => {
  if (!target || typeof target !== 'object') return target
  const entries = patch && typeof patch === 'object' ? Object.entries(patch) : []
  for (const [path, value] of entries) {
    const segments = path.split('.')
    let cursor = target
    for (let i = 0; i < segments.length; i++) {
      const key = segments[i]
      if (i === segments.length - 1) {
        cursor[key] = value
      } else {
        if (!cursor[key] || typeof cursor[key] !== 'object') {
          cursor[key] = {}
        }
        cursor = cursor[key]
      }
    }
  }
  return target
}

export const useCharacterStore = defineStore('characters', {
  state: () => ({
    characters: [],
    sharedCharacters: [],
    currentCharacter: null,
    loading: false,
    error: null,
    cacheInfo: null,
    characterCacheInfo: {},
    sharedCharactersCacheInfo: null,
    // Default cache duration in minutes (0 means no caching)
    cacheDuration: 5 // Short cache for characters as they change often
  }),

  getters: {
    getCharacters: (state) => structuredCloneSafe(state.characters),
    getSharedCharacters: (state) => structuredCloneSafe(state.sharedCharacters),
    getCurrentCharacter: (state) => structuredCloneSafe(state.currentCharacter),
    isLoading: (state) => !!state.loading,
    getError: (state) => state.error,
    getCacheDuration: (state) => state.cacheDuration
  },

  actions: {
    setCacheDuration(minutes) {
      this.cacheDuration = minutes
    },

    // ----- List cache helpers (store-owned) -----
    _listCacheKey(userId) {
      return userId ? `characters-list-${userId}` : null
    },
    _summarizeForList(c) {
      if (!c) return null
      try {
        // Minimal, list-friendly summary
        const classString = Array.isArray(c.classes)
          ? c.classes.map((cl) => `${cl.name || ''} ${cl.level || 0}`.trim()).join(', ')
          : ''
        return {
          id: c.id,
          name: c.name,
          avatar: c.avatar,
          gameVersion: c.gameVersion,
          level: c.level,
          classesText: classString,
          updatedAt: c.updatedAt || c.updated || Date.now()
        }
      } catch {
        return { id: c.id, name: c.name }
      }
    },
    _hydrateListFromLocal(userId) {
      const key = this._listCacheKey(userId)
      if (!key) return false
      try {
        const raw = localStorage.getItem(key)
        if (!raw) return false
        const parsed = JSON.parse(raw)
        const items = Array.isArray(parsed?.items) ? parsed.items : []
        if (items.length) {
          // NOTE: this hydrates list with summaries; fetchCharacters will still pull full objects
          // Defensive: dedupe items by id to avoid duplicate entries in local cache causing UI flashes
          const seen = new Set()
          const deduped = []
          for (const it of items) {
            if (!it || !it.id) continue
            if (seen.has(it.id)) continue
            seen.add(it.id)
            deduped.push(it)
          }
          this.characters = deduped
          // Do NOT update cacheInfo here. Hydration provides a fast first paint, but we
          // still want fetchCharacters() to revalidate against the server and refresh
          // the local list cache. cacheInfo should only be set after a successful server fetch.
          return true
        }
        return false
      } catch {
        return false
      }
    },
    _saveListToLocal(userId, list) {
      const key = this._listCacheKey(userId)
      if (!key) return
      try {
        const items = (list || []).map((c) => this._summarizeForList(c)).filter(Boolean)
        localStorage.setItem(key, JSON.stringify({ updatedAt: Date.now(), items }))
      } catch {
        // ignore persistence errors
      }
    },
    _updateListCacheEntry(userId, id, partial) {
      const key = this._listCacheKey(userId)
      if (!key || !id) return
      try {
        const raw = localStorage.getItem(key)
        const parsed = raw ? JSON.parse(raw) : { updatedAt: 0, items: [] }
        const items = Array.isArray(parsed.items) ? parsed.items : []
        const idx = items.findIndex((c) => c && c.id === id)
        const merged = {
          ...(idx !== -1 ? items[idx] : {}),
          ...(this._summarizeForList(partial) || partial)
        }
        if (idx !== -1) items[idx] = merged
        else items.push(merged)
        localStorage.setItem(key, JSON.stringify({ updatedAt: Date.now(), items }))
      } catch {
        // ignore
      }
    },
    _removeFromListCache(userId, id) {
      const key = this._listCacheKey(userId)
      if (!key || !id) return
      try {
        const raw = localStorage.getItem(key)
        if (!raw) return
        const parsed = JSON.parse(raw)
        const items = Array.isArray(parsed?.items) ? parsed.items : []
        const next = items.filter((c) => c && c.id !== id)
        localStorage.setItem(key, JSON.stringify({ updatedAt: Date.now(), items: next }))
      } catch {
        // ignore
      }
    },

    async fetchCharacters(forceRefresh = false) {
      const userStore = useUserStore()
      const userId = userStore.userId
      if (!userId) return []

      // If we have a valid in-memory cache and not forced, use it
      if (
        !forceRefresh &&
        isCacheValid(this.cacheInfo, this.cacheDuration) &&
        this.characters.length
      ) {
        return this.characters
      }

      // Try hydrating from local list cache for immediate UI (offline/first paint)
      if (!this.characters.length) {
        this._hydrateListFromLocal(userId)
      }

      try {
        this.loading = true
        this.error = null
        const response = await CharacterService.getCharacters(userId)
        // Defensive: ensure no duplicate character entries (by id) are stored in the list
        if (Array.isArray(response)) {
          const seen = new Set()
          const deduped = []
          for (const c of response) {
            if (!c || !c.id) continue
            if (seen.has(c.id)) continue
            seen.add(c.id)
            deduped.push(c)
          }
          // store per-item targeted clones to avoid leaking server references
          this.characters = deduped.map((it) => targetedCloneForMutation(it))
        } else {
          // single non-array response: defensively clone per-item
          this.characters = Array.isArray(response)
            ? response.map((it) => targetedCloneForMutation(it))
            : response
        }
        this.cacheInfo = createCacheInfo()
        // Persist list summary to local cache
        this._saveListToLocal(userId, this.characters)
        return this.characters
      } catch (error) {
        this.error = error.message || 'Failed to fetch characters'
        console.error('Error fetching characters:', error)
        // Fall back to local list cache content if available
        if (!this.characters.length) this._hydrateListFromLocal(userId)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch characters for a specific user ID (not necessarily the current user)
     * Used by admins/moderators to fetch other players' characters
     * @param {string} userId - The user ID to fetch characters for
     * @returns {Promise<Array>} - Array of character objects
     */
    async fetchCharactersByUserId(userId) {
      if (!userId) {
        console.warn('fetchCharactersByUserId: No userId provided')
        return []
      }

      try {
        const response = await CharacterService.getCharacters(userId)
        if (Array.isArray(response)) {
          const seen = new Set()
          const deduped = []
          for (const c of response) {
            if (!c || !c.id) continue
            if (seen.has(c.id)) continue
            seen.add(c.id)
            deduped.push(c)
          }
          return deduped.map((it) => targetedCloneForMutation(it))
        }
        return Array.isArray(response) ? response.map((it) => targetedCloneForMutation(it)) : []
      } catch (error) {
        console.error('Error fetching characters by user ID:', error)
        throw error
      }
    },

    async fetchSharedCharacters(forceRefresh = false) {
      // Get user ID from UserStore
      const userStore = useUserStore()
      const userId = userStore.userId

      // If no user ID, return empty array
      if (!userId) {
        return []
      }

      // Return cached data if it's still valid and no force refresh
      if (!forceRefresh && isCacheValid(this.sharedCharactersCacheInfo, this.cacheDuration)) {
        return this.sharedCharacters
      }

      try {
        this.loading = true
        this.error = null

        const response = await CharacterService.getSharedCharacters(userId)
        this.sharedCharacters = Array.isArray(response)
          ? response.map((it) => targetedCloneForMutation(it))
          : response

        // Update cache info
        this.sharedCharactersCacheInfo = createCacheInfo()

        return this.sharedCharacters
      } catch (error) {
        this.error = error.message || 'Failed to fetch shared characters'
        console.error('Error fetching shared characters:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchCharacter(id, forceRefresh = false) {
      const shouldFetchServer =
        forceRefresh ||
        this.currentCharacter?.id !== id ||
        !isCacheValid(this.characterCacheInfo[id], this.cacheDuration)

      if (!shouldFetchServer) {
        return this.currentCharacter
      }

      try {
        this.loading = true
        this.error = null

        const response = await CharacterService.getCharacter(id)
        this.currentCharacter = response

        // Update cache info for this character
        this.characterCacheInfo = {
          ...this.characterCacheInfo,
          [id]: createCacheInfo()
        }

        return this.currentCharacter
      } catch (error) {
        this.error = error.message || `Failed to fetch character with ID: ${id}`
        console.error(`Error fetching character with ID: ${id}:`, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async saveCharacter(character, schema = null) {
      try {
        this.loading = true
        this.error = null
        const userStore = useUserStore()
        const userId = userStore.userId

        if (!character.id && userId) {
          character.owner = userId
        }

        const savedCharacter = await CharacterService.saveCharacter(character, schema)

        if (this.currentCharacter?.id === savedCharacter.id) {
          this.currentCharacter = structuredCloneSafe(savedCharacter)
          this.characterCacheInfo = {
            ...this.characterCacheInfo,
            [savedCharacter.id]: createCacheInfo()
          }
        }

        const isOwnedByCurrentUser = (() => {
          if (!userId) return false
          const resolveOwnerId = (obj) =>
            obj?.owner || obj?.userId || obj?.createdBy || null
          const ownerId = resolveOwnerId(savedCharacter) || resolveOwnerId(character)
          return !!ownerId && ownerId === userId
        })()

        // Only reflect in the user's list/cache when the saved character belongs to them
        if (isOwnedByCurrentUser) {
          const index = this.characters.findIndex((char) => char && char.id === savedCharacter.id)
          if (index !== -1) this.characters[index] = structuredCloneSafe(savedCharacter)
          else this.characters.push(structuredCloneSafe(savedCharacter))
          this.cacheInfo = createCacheInfo()
          // Update local list cache with summary of saved character
          try {
            this._updateListCacheEntry(userId, savedCharacter.id, savedCharacter)
          } catch {
            /* noop */
          }
        }

        return savedCharacter
      } catch (error) {
        this.error = error.message || 'Failed to save character'
        console.error('Error saving character:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async saveCharacterPatch(id, patch) {
      if (!id || !patch || typeof patch !== 'object') return null
      try {
        this.loading = true
        this.error = null
        const saved = await CharacterService.updateCharacterFields(id, patch)

        if (this.currentCharacter?.id === id) {
          applyPatchToObject(this.currentCharacter, patch)
          this.characterCacheInfo = {
            ...this.characterCacheInfo,
            [id]: createCacheInfo()
          }
        }

        const userId = useUserStore().userId
        const ownerSource =
          (this.currentCharacter?.id === id ? this.currentCharacter : null) ||
          this.characters.find((char) => char && char.id === id) ||
          null
        const ownerId = ownerSource?.owner || ownerSource?.userId || ownerSource?.createdBy || null
        const isOwnedByCurrentUser = !!ownerId && !!userId && ownerId === userId

        if (isOwnedByCurrentUser) {
          const index = this.characters.findIndex((char) => char && char.id === id)
          if (index !== -1) {
            applyPatchToObject(this.characters[index], patch)
          }
          this.cacheInfo = createCacheInfo()
        }

        // Update local list cache with summary of patched character
        try {
          if (isOwnedByCurrentUser) {
            this._updateListCacheEntry(userId, id, patch)
          }
        } catch {
          /* noop */
        }

        return saved || { id, ...patch }
      } catch (error) {
        this.error = error.message || 'Failed to save character changes'
        console.error('Error saving character patch:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async deleteCharacter(id) {
      try {
        this.loading = true
        this.error = null
        await CharacterService.deleteCharacter(id)
        this.characters = this.characters.filter((char) => char.id !== id)
        this.sharedCharacters = this.sharedCharacters.filter((char) => char.id !== id)
        if (this.characterCacheInfo[id]) delete this.characterCacheInfo[id]
        if (this.currentCharacter?.id === id) this.currentCharacter = null
        this.cacheInfo = createCacheInfo()
        // Remove from local list cache
        try {
          const userId = useUserStore().userId
          this._removeFromListCache(userId, id)
        } catch {
          /* noop */
        }
        return true
      } catch (error) {
        this.error = error.message || `Failed to delete character with ID: ${id}`
        console.error(`Error deleting character with ID: ${id}:`, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async shareCharacter(characterId, userIds) {
      try {
        this.loading = true
        this.error = null

        await CharacterService.shareCharacter(characterId, userIds)

        // Update cache to reflect sharing changes
        this.clearCharacterCache(characterId)

        return true
      } catch (error) {
        this.error = error.message || `Failed to share character`
        console.error(`Error sharing character:`, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    // Apply a local (optimistic) partial patch to currentCharacter and list, updating cache timestamp
    applyLocalPatch(id, partial) {
      if (!id || !partial || typeof partial !== 'object') return
      if (this.currentCharacter && this.currentCharacter.id === id) {
        Object.assign(this.currentCharacter, partial)
      }
      const idx = this.characters.findIndex((c) => c.id === id)
      if (idx !== -1) {
        Object.assign(this.characters[idx], partial)
      }
      this.characterCacheInfo = {
        ...this.characterCacheInfo,
        [id]: createCacheInfo()
      }
      // Also update the list cache summary minimally
      try {
        const userId = useUserStore().userId
        this._updateListCacheEntry(userId, id, partial)
      } catch {
        /* ignore */
      }
    },

    clearCache() {
      this.cacheInfo = null
      this.sharedCharactersCacheInfo = null
      this.characterCacheInfo = {}
      this.currentCharacter = null
    },

    clearCharacterCache(id) {
      if (id) {
        if (this.characterCacheInfo[id]) {
          delete this.characterCacheInfo[id]
        }
        if (this.currentCharacter?.id === id) {
          this.currentCharacter = null
        }
      } else {
        this.characterCacheInfo = {}
        this.currentCharacter = null
      }
    },

  }
})
